/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { useState } from 'react'
import { useWebApp } from '@/hooks/webapp'
import { useRouter } from 'next/navigation'

// import server actions
import { updateAction } from '@/actions/webapps/update'

// import components
import { Suspense } from 'react'
import { Input, Textarea, TagInput } from '@/components/input'
import { ClientOnly } from '@/components/client'
import { MarkdownInput } from '@/components/editor'
import { FlexButton } from '@/components/buttons'
import {
	Field,
	Fieldset,
	Label,
	Legend
} from '@headlessui/react'

export const GeneralSettings = () => {
	const router = useRouter()
	const webApp = useWebApp()

	// create state
	const [name, setName] = useState(webApp.name)
	const [url, setUrl] = useState(webApp.url)
	const [summary, setSummary] = useState(webApp.summary ?? undefined)
	const [description, setDescription] = useState(webApp.description ?? undefined)
	const [instructions, setInstructions] = useState(webApp.instructions ?? undefined)
	const [tags, setTags] = useState(webApp.tags)

	// update action
	const update = async () => {
		await updateAction({
			id: webApp.id,
			name,
			url,
			summary: summary,
			description: description,
			instructions: instructions,
			tags
		})
		router.refresh()
	}

	return (
		<Fieldset as='form' className='flex flex-col gap-3 p-3' action={update}>
			<div className='flex flex-col'>
				<Legend className='text-xl font-semibold'>General Settings</Legend>
				<span className='text-xs font-light'>Describes how the web app will be shown in various interfaces.</span>
			</div>
			<Field className='flex items-stretch gap-5'>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Display Name</Label>
					<span className='text-xs font-light'>The full name of the web app.</span>
				</div>
				<div className='grow'>
					<Input readOnly={!webApp.canEdit} required placeholder='Name...' value={name} onChange={e => setName(e.target.value)} />
				</div>
			</Field>
			<Field className='flex items-stretch gap-5'>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>URL</Label>
					<span className='text-xs font-light'>The URL to open the web app or 360 tour.</span>
				</div>
				<div className='grow'>
					<Input readOnly={!webApp.canEdit} required placeholder='URL...' value={url} onChange={e => setUrl(e.target.value)} />
				</div>
			</Field>
			<Field className='flex items-stretch gap-5'>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Summary</Label>
					<span className='text-xs font-light'>A brief summary of what the application does.</span>
				</div>
				<div className='grow'>
					<Textarea readOnly={!webApp.canEdit} placeholder='Summary...' value={summary} onChange={e => setSummary(e.target.value)} />
				</div>
			</Field>
			<Field className='flex items-stretch gap-5'>
				<div className='flex w-64 shrink-0 flex-col'>
					<Label className='font-semibold'>Description</Label>
					<span className='text-xs font-light'>A detailed explanation of the application&#39;s features and functionality.</span>
				</div>
				<div className='grow'>
					<Suspense fallback={<>{description}</>}>
						<ClientOnly>
							<MarkdownInput placeholder='Description...' markdown={description ?? ''} onChange={setDescription} />
						</ClientOnly>
					</Suspense>
				</div>
			</Field>
			<Field className='flex items-stretch gap-5'>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Instructions</Label>
					<span className='text-xs font-light'>Guidelines or steps on how to use the application.</span>
				</div>
				<div className='grow'>
					<Textarea readOnly={!webApp.canEdit} placeholder='Instructions...' value={instructions} onChange={e => setInstructions(e.target.value)} />
				</div>
			</Field>
			<Field className='flex items-stretch gap-5'>
				<div className='flex w-64 shrink-0 flex-col'>
					<Label className='font-semibold'>Tags</Label>
					<span className='text-xs font-light'>Keywords associated with the application to help categorize and find it easily.</span>
				</div>
				<div className='grow'>
					<TagInput disabled={!webApp.canEdit} values={tags} onUpdate={setTags} />
				</div>
			</Field>
			<div className='flex items-stretch gap-5'>
				<div className='flex w-64 flex-col'></div>
				<FlexButton type='submit' disabled={!webApp.canEdit}>Save Changes</FlexButton>
			</div>
		</Fieldset>
	)
}